import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'src/@vymo/ui/atoms';
import Text from 'src/@vymo/ui/atoms/text';
import { Form } from 'src/@vymo/ui/molecules';
import {
  FormVersion,
  InputFieldConfig,
} from 'src/@vymo/ui/molecules/form/types';
import { ReactComponent as NoMeeting } from 'src/assets/icons/noMeetings.svg';
import { Keys, locale } from 'src/i18n';
import { useAppSelector } from 'src/store/hooks';
import { getLeadNextStates } from '../queries';
import { getCalendarActivityDetails, getLeadDetails } from '../selectors';
import styles from './index.module.scss';

type ActivityDetailsProps = {
  onAction: (action: string, module?: string, vo?: string) => void;
  calendarItem?: Record<string, any> | null;
  isActivityCompleted: boolean;
};

function ActivityDetails({
  onAction,
  calendarItem,
  isActivityCompleted,
}: ActivityDetailsProps) {
  const formRef = useRef(null);

  const [selectedActivityData, setSelectedActivityData] = useState(null);

  const [name, setName] = useState('');

  const calendarItemData = useAppSelector(getCalendarActivityDetails);
  const leadData = useAppSelector(getLeadDetails);

  const activityModuleCode =
    _.get(calendarItemData, 'data.vo.vo_module.code') || 'user';

  const pendingApprovalStatus = calendarItem?.approval_status?.pending_action;

  useEffect(() => {
    if (calendarItemData?.info?.inputs && calendarItem) {
      setSelectedActivityData(calendarItemData.info.inputs);
      setName(calendarItemData.name);
      return;
    }

    if (leadData?.inputs) {
      setSelectedActivityData(leadData.inputs);
      setName(leadData.name);
      return;
    }

    setSelectedActivityData(null);
  }, [calendarItemData, leadData, calendarItem]);

  const nextStates = getLeadNextStates(leadData);

  const canUpdate =
    pendingApprovalStatus || nextStates.length === 0 || isActivityCompleted;

  const fields: InputFieldConfig[] = useMemo(
    () => selectedActivityData || [],
    [selectedActivityData],
  );

  const formValues = useMemo(() => {
    if (!fields.length) return {};

    const values: Record<string, any> = {};
    fields.forEach((field) => {
      if (field.value !== undefined) {
        values[field.code] = {
          value: field.value,
          code: field.code,
        };
      }
    });
    return values;
  }, [fields]);

  return (
    <>
      {/* SHOW EMPTY UI WHEN NO ACTIVITY IS SELECTED */}
      {!selectedActivityData ? (
        <div className={styles.activityCalendarCard__content__noSelection}>
          <NoMeeting />
          <Text>{locale(Keys.ACTIVITY_CARD_PLACEHOLDER)}</Text>
        </div>
      ) : (
        <div className={styles.activityDetails}>
          {/* HEADER */}
          <div className={styles.activityDetails__header}>
            <Text type="h5">{name}</Text>
            <Button
              disabled={canUpdate}
              type="outlined"
              variant="subtle"
              onClick={() =>
                onAction(
                  'update_task',
                  activityModuleCode,
                  calendarItemData.code,
                )
              }
            >
              Update
            </Button>
          </div>

          {/* FORM CONTENT */}
          {fields.length > 0 && (
            <div className={styles.activityDetails__content}>
              <Form
                ref={formRef}
                id="activityDetails"
                name="activityDetails"
                onChange={() => {}}
                config={{
                  version: FormVersion.web,
                  data: fields,
                  grouping: [],
                  fieldItemConfig: { showDisabledIcon: true },
                  viewMode: true,
                }}
                className={styles.activityDetails__form}
                value={formValues}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default ActivityDetails;
